import React from 'react';

import { Button, Group, Stack, Title, TextInput, LoadingOverlay } from '@mantine/core';
import { useForm } from '@mantine/form';
import { useShallowEffect } from '@mantine/hooks';
import Drawer from 'components/Drawer';
import TaxRatePercentageInput from 'components/TaxRatePercentageInput';
import useCreateTaxRate from 'hooks/taxRate/useCreateTaxRate';
import useTaxRate from 'hooks/taxRate/useTaxRate';
import useUpdateTaxRate from 'hooks/taxRate/useUpdateTaxRate';
import notificationHandler from 'utils/notifications';
import { v4 as uuidv4 } from 'uuid';

interface FormValues {
  taxRateId: string;
  name: string;
  rate: number;
}

interface IDrawerTaxRateFormProps {
  formId: string;
  title: JSX.Element;
  opened: boolean;
  closeHandler: () => void;
  taxRateId?: string;
  type: 'create' | 'edit';
  permission?: boolean;
}

const DrawerTaxRateForm = ({
  opened,
  closeHandler,
  formId,
  title,
  taxRateId,
  type,
  permission = true,
}: IDrawerTaxRateFormProps): JSX.Element => {
  const createMutation = useCreateTaxRate();
  const updateMutation = useUpdateTaxRate();
  const { data, isLoading, isFetching, isSuccess } = useTaxRate(taxRateId);

  const form = useForm<FormValues>({
    initialValues: {
      taxRateId: '',
      name: '',
      rate: 0,
    },

    validate: {
      name: value => (value.length < 3 || value.length > 50 ? 'Name must be between 3-50 characters' : null),
      rate: value => (value < 0 || value > 100 ? 'Percentage must be between 0-100' : null),
    },

    transformValues: values => ({
      ...values,
      taxRateId: type === 'create' ? uuidv4() : values.taxRateId,
      rate: values.rate / 100,
    }),
  });

  const handleCloseAndReset = (): void => {
    closeHandler();
    form.reset();
  };

  const handleSubmit = (payload: FormValues): void => {
    const { successNotification, errorNotification } = notificationHandler();
    if (type === 'edit') {
      updateMutation.mutate(payload, {
        onSuccess: response => {
          successNotification('Tax rate update success', `${response.data.name} successfully updated.`);
          handleCloseAndReset();
        },
        onError: error => {
          errorNotification('Tax rate update failed', error.response?.data.message);
        },
      });
    } else {
      createMutation.mutate(payload, {
        onSuccess: response => {
          successNotification('Tax rate creation success', `${response.data.name} successfully created.`);
          handleCloseAndReset();
        },
        onError: error => {
          errorNotification('Tax rate creation failed', error.response?.data.message);
        },
      });
    }
  };

  useShallowEffect(() => {
    if (type === 'edit' && data?.data && isSuccess) {
      form.setValues({ ...data.data, rate: data.data.rate * 100 });
    }
  }, [data]);

  return (
    <Drawer
      opened={opened}
      onClose={handleCloseAndReset}
      title={<Title order={3}>{title}</Title>}
      footer={
        <Group position='right'>
          <Button variant='subtle' onClick={handleCloseAndReset}>
            Cancel
          </Button>
          <Button
            form={formId}
            type='submit'
            disabled={!permission}
            loading={createMutation.isLoading || updateMutation.isLoading || isLoading || isFetching}
          >
            Save
          </Button>
        </Group>
      }
    >
      <LoadingOverlay visible={type === 'edit' && (isLoading || isFetching)} />
      <form id={formId} onSubmit={form.onSubmit(handleSubmit)}>
        <Stack>
          <TextInput
            {...form.getInputProps('name')}
            label='Name'
            placeholder='Set Tax Rate Name'
            required
            disabled={!permission}
          />
          <TaxRatePercentageInput
            {...form.getInputProps('rate')}
            label='Percentage'
            placeholder='Set tax rate'
            required
            disabled={!permission}
          />
        </Stack>
      </form>
    </Drawer>
  );
};

export default DrawerTaxRateForm;
